import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, ArrowLeft } from "lucide-react";
import { setStaffCookie } from "../lib/staffCookie";
import StaffShell from "../components/staff/StaffShell";
import EvidenceForm from "../components/contract/EvidenceForm";
import PreflightPanel from "../components/contract/PreflightPanel";
import CoveragePanel from "../components/contract/CoveragePanel";
import FindingsEditor from "../components/contract/FindingsEditor";
import ReviewHistory from "../components/contract/ReviewHistory";
import { API, IN_FLIGHT, STATUS_LABEL, fmtDate } from "../components/contract/reviewMeta";

const POLL_INTERVAL_MS = 3000;
const MAX_POLL_MS = 15 * 60 * 1000;
const EXTEND_POLL_MS = 10 * 60 * 1000;
const EMPTY_FORM = { entryNumber: "", file: null, emailFiles: [], emailText: "" };

function toReviewable(job) {
  const decided = new Map((job.decisions || []).map((d) => [d.issue, d]));
  const hasDecisions = decided.size > 0;
  return (job.findings || []).map((f) => {
    const d = decided.get(f.issue);
    return {
      ...f,
      _include: hasDecisions ? !!(d && d.included) : true,
      _edited: (d && d.comment) || f.suggested_comment || "",
      _clause: (d && d.clause) || f.proposed_clause || "",
      _decided: hasDecisions,
    };
  });
}

/**
 * Contract review: evidence pack -> pre-flight gate -> sectioned
 * analysis + verifier -> coverage report + findings editor -> history.
 */
export default function ContractReview() {
  const [form, setForm] = useState(EMPTY_FORM);
  const [phase, setPhase] = useState("form"); // form | preflight | running | done | history
  const [busy, setBusy] = useState(false);
  const [preflight, setPreflight] = useState(null);
  const [job, setJob] = useState(null);
  const [reviewable, setReviewable] = useState([]);
  const [summary, setSummary] = useState(null);
  const [error, setError] = useState(null);
  const [timedOut, setTimedOut] = useState(false);
  const [historyKey, setHistoryKey] = useState(0);
  const pollTimer = useRef(null);
  const pollDeadline = useRef(0);

  useEffect(() => {
    setStaffCookie();
    return () => pollTimer.current && clearTimeout(pollTimer.current);
  }, []);

  const stopPolling = () => pollTimer.current && clearTimeout(pollTimer.current);

  const poll = useCallback(async (id) => {
    try {
      const { data } = await axios.get(`${API}/api/proposals/contract-review/${id}`, { timeout: 15000 });
      setJob(data);
      if (data.status === "done") {
        setReviewable(toReviewable(data));
        setSummary(data.summary || null);
        setPhase("done");
        setHistoryKey((k) => k + 1);
        return;
      }
      if (data.status === "error") {
        setError(data.error || "Contract review failed.");
        toast.error(data.error || "Contract review failed.");
        setPhase("preflight");
        return;
      }
      if (Date.now() > pollDeadline.current) return setTimedOut(true);
      pollTimer.current = setTimeout(() => poll(id), POLL_INTERVAL_MS);
    } catch (err) {
      if (err?.response?.status === 404) {
        setError("The review job is no longer available, please start again from the evidence pack.");
        setPhase("form");
        return;
      }
      if (Date.now() > pollDeadline.current) return setTimedOut(true);
      pollTimer.current = setTimeout(() => poll(id), POLL_INTERVAL_MS);
    }
  }, []);

  // Deep link (?job=) from the SOW register: reviews started by a
  // client upload in the proposal viewer open here, running or done.
  const deepLinkJob = new URLSearchParams(window.location.search).get("job");
  useEffect(() => {
    if (!deepLinkJob) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/api/proposals/contract-review/${deepLinkJob}`, { timeout: 15000 });
        if (cancelled) return;
        setJob(data);
        if (data.status === "done") {
          setReviewable(toReviewable(data));
          setSummary(data.summary || null);
          setPhase("history");
        } else if (IN_FLIGHT.has(data.status)) {
          setPhase("running");
          pollDeadline.current = Date.now() + MAX_POLL_MS;
          pollTimer.current = setTimeout(() => poll(deepLinkJob), POLL_INTERVAL_MS);
        } else {
          setError(data.error || `Review is ${data.status}.`);
        }
      } catch {
        if (!cancelled) toast.error("Could not load that review.");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [deepLinkJob, poll]);

  const onCheckEvidence = async (e) => {
    e.preventDefault();
    setError(null);
    const n = parseInt(form.entryNumber, 10);
    if (!Number.isFinite(n) || n <= 0) return toast.error("Enter a valid proposal number first.");
    if (!form.file) return toast.error("Please choose a .docx or .pdf contract to upload.");
    const fd = new FormData();
    fd.append("file", form.file);
    form.emailFiles.forEach((f) => fd.append("emails", f));
    if (form.emailText.trim()) fd.append("email_text", form.emailText.trim());
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/api/proposals/${n}/contract-review/preflight`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 240000,
      });
      setPreflight(data);
      setJob({ job_id: data.job_id, status: "preflight" });
      setPhase("preflight");
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || "Could not check the evidence pack.";
      setError(typeof detail === "string" ? detail : JSON.stringify(detail));
      toast.error(typeof detail === "string" ? detail : "Could not check the evidence pack.");
    } finally {
      setBusy(false);
    }
  };

  const onStart = async (body) => {
    if (!preflight?.job_id) return;
    setBusy(true);
    setError(null);
    try {
      await axios.post(`${API}/api/proposals/contract-review/${preflight.job_id}/start`, body, { timeout: 30000 });
      setPhase("running");
      setTimedOut(false);
      setJob((j) => ({ ...(j || {}), status: "queued", progress: "Queued" }));
      pollDeadline.current = Date.now() + MAX_POLL_MS;
      pollTimer.current = setTimeout(() => poll(preflight.job_id), POLL_INTERVAL_MS);
      toast.success("Review started. Long contracts and email chains can take several minutes.");
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || "Could not start the review.";
      toast.error(typeof detail === "string" ? detail : "Could not start the review.");
    } finally {
      setBusy(false);
    }
  };

  const keepWaiting = () => {
    if (!job?.job_id) return;
    setTimedOut(false);
    pollDeadline.current = Date.now() + EXTEND_POLL_MS;
    poll(job.job_id);
  };

  const openHistory = async (id) => {
    stopPolling();
    setBusy(true);
    try {
      const { data } = await axios.get(`${API}/api/proposals/contract-review/${id}`);
      setJob(data);
      setReviewable(toReviewable(data));
      setSummary(data.summary || null);
      setPhase("history");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch {
      toast.error("Could not load that review.");
    } finally {
      setBusy(false);
    }
  };

  const startNew = () => {
    stopPolling();
    setForm(EMPTY_FORM);
    setPreflight(null);
    setJob(null);
    setReviewable([]);
    setSummary(null);
    setError(null);
    setTimedOut(false);
    setPhase("form");
  };

  const running = phase === "running" && job && IN_FLIGHT.has(job.status);

  return (
    <StaffShell max="wide">
      <div data-testid="contract-review-page" className="flex flex-col gap-8 max-w-5xl mx-auto w-full">
        <header className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <p className="text-[10px] font-mono tracking-[0.2em] uppercase text-[#1B4F8A] mb-2">Internal, pre-signature</p>
            <h1 className="text-3xl sm:text-4xl font-serif font-light text-slate-900 mb-3">Contract review</h1>
            <p className="text-sm text-slate-500 max-w-2xl leading-relaxed">
              Every review reads four sources: the client draft, the Medicys proposal, the terms and conditions
              sent with it, and the email correspondence. Claude checks each clause both ways, a second pass
              verifies the findings and produces a coverage checklist, and you decide which comments go into
              the Word document.
            </p>
          </div>
          {phase !== "form" && (
            <button type="button" onClick={startNew} data-testid="contract-review-new-btn" className="inline-flex items-center gap-2 text-xs font-mono tracking-[0.1em] uppercase text-stone-500 hover:text-slate-900 border border-stone-300 rounded-md px-3 py-2">
              <ArrowLeft className="w-3.5 h-3.5" /> New review
            </button>
          )}
        </header>

        {phase === "form" && <EvidenceForm state={form} setState={setForm} busy={busy} onSubmit={onCheckEvidence} />}

        {phase === "preflight" && preflight && (
          <PreflightPanel preflight={preflight} onStart={onStart} onBack={() => setPhase("form")} starting={busy} />
        )}

        {running && !timedOut && (
          <div className="bg-white border border-stone-200 rounded-xl p-5 flex items-center gap-3 shadow-sm" data-testid="contract-review-progress">
            <Loader2 className="w-5 h-5 animate-spin text-[#1B4F8A]" />
            <div className="text-sm text-slate-700">
              <p className="font-medium" data-testid="contract-review-progress-label">{job.progress || STATUS_LABEL[job.status] || "Working…"}</p>
              <p className="text-xs text-stone-500 mt-0.5">
                Reading the correspondence, then the contract (in sections when it is long), then a verification
                pass that builds the coverage checklist. You can leave the page open, the review keeps running.
              </p>
            </div>
          </div>
        )}

        {timedOut && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 flex flex-col sm:flex-row sm:items-center gap-4" data-testid="contract-review-timed-out">
            <div className="flex-1 text-sm text-amber-800">
              <p className="font-semibold mb-1">Still analysing…</p>
              <p className="text-xs text-amber-700/80">The review is taking longer than usual but is still running. Press Keep waiting to extend the poll for another 10 minutes.</p>
            </div>
            <button type="button" onClick={keepWaiting} data-testid="contract-review-keep-waiting" className="px-4 py-2 rounded-full bg-amber-100 hover:bg-amber-200 border border-amber-300 text-amber-800 text-xs font-mono tracking-[0.14em] uppercase shrink-0">
              Keep waiting
            </button>
          </div>
        )}

        {error && (
          <div className="bg-rose-50 border border-rose-200 rounded-xl p-5 text-sm text-rose-800" data-testid="contract-review-error">
            <p className="font-semibold mb-1">Review failed</p>
            <p className="text-rose-700/80">{error}</p>
          </div>
        )}

        {(phase === "done" || phase === "history") && job?.status === "done" && (
          <>
            {phase === "history" && (
              <div className="rounded-xl border border-[#3EA8DC]/40 bg-[#3EA8DC]/10 px-4 py-3 text-xs text-slate-700" data-testid="contract-review-history-banner">
                Viewing a previous review of proposal <span className="font-mono">#{job.entry_number}</span> ({job.filename_in}), run {fmtDate(job.created_at)} by {job.created_by || "staff"}.
                {job.no_correspondence_reason ? ` No correspondence was supplied: ${job.no_correspondence_reason}` : ""}
              </div>
            )}
            <CoveragePanel job={job} readOnly={phase === "history"} />
            <FindingsEditor
              job={job}
              reviewable={reviewable}
              setReviewable={setReviewable}
              summary={summary}
              setSummary={setSummary}
              readOnly={phase === "history"}
              onBuilt={() => setHistoryKey((k) => k + 1)}
            />
          </>
        )}

        <ReviewHistory onOpen={openHistory} refreshKey={historyKey} entryFilter="" />
      </div>
    </StaffShell>
  );
}
